import { IonSkeletonText, useIonAlert } from '@ionic/react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import ActivityOverviewTile from "./ActivityOverviewTile";
import { isRfidLastSeenHidden, useHideRfidLastSeenMutation, useRfidLastSeenQuery, useSetRfidLastSeenLocationMutation } from '../query/rfidLastSeenQuery';
import { useRfidProfileQuery } from '../query/rfidProfileQuery';
import { useRfidProfileAvatarQuery } from '../query/rfidProfileAvatarQuery';
import styles from './ActivityOverview.module.css';
import { useDeviceEventQuery } from '../query/deviceEventQuery';
import OCGatewayClient from '../client/OCGatewayClient';
import { useCommonTranslation, useComponentTranslation, usePageTranslation } from '../hooks/useTranslation';
import type { SubEventAction, SubEventDirection } from '../models/EventSummary';
import type { RfidLastSeen, RfidLastSeenLocation } from '../models/RfidLastSeen';
import useUX from '../utils/ux';

type ActivityStatus = {
    status: string;
    isInside: boolean;
};

interface ActivityOverviewProps {
    onShowRfidEvents?: (rfidCode: string) => void;
}

type HideRfidLastSeenParams = Pick<RfidLastSeen, 'deviceId' | 'rfidCode' | 'eventId'>;
type SetRfidLastSeenLocationParams = Pick<RfidLastSeen, 'deviceId' | 'rfidCode'> & {
    location: RfidLastSeenLocation;
};

type ActivityItemProps = RfidLastSeen & ActivityOverviewProps & {
    onHideRfidLastSeen?: (params: HideRfidLastSeenParams) => void;
    onSetRfidLastSeenLocation?: (params: SetRfidLastSeenLocationParams) => void;
    isHidePending?: boolean;
    isSetLocationPending?: boolean;
};

function getOutcomeDirection(lastSubevent: RfidLastSeen['lastSubevent']): 'inward' | 'outward' | 'peek-inward' | 'peek-outward' | 'deny-inward' | 'deny-outward' | undefined {
    if (!lastSubevent) {
        return undefined;
    }

    if (lastSubevent.action === 'PEEK') {
        return lastSubevent.direction === 'INWARD' ? 'peek-inward' : 'peek-outward';
    }

    if (lastSubevent.action === 'DENY') {
        return lastSubevent.direction === 'INWARD' ? 'deny-inward' : 'deny-outward';
    }

    if (lastSubevent.action === 'TRANSIT' || lastSubevent.action === 'BREACH') {
        return lastSubevent.direction === 'INWARD' ? 'inward' : 'outward';
    }

    return undefined;
}

function getActivityStatus(
    location: RfidLastSeen['location'],
    locationSource: RfidLastSeen['locationSource'],
    lastSubevent: RfidLastSeen['lastSubevent'],
    tPages: (key: string) => string,
    tComponents: (key: string) => string
): ActivityStatus {
    if (locationSource === 'MANUAL' && location) {
        const locationKey = location === 'INSIDE' ? 'marked_inside' : 'marked_outside';

        return {
            status: tComponents(`activity_overview_tile.${locationKey}`),
            isInside: location === 'INSIDE'
        };
    }

    if (!lastSubevent || !location) {
        return {
            status: tComponents('unknown'),
            isInside: false
        };
    }

    const statusKey = `${lastSubevent.action}_${lastSubevent.direction}` as `${SubEventAction}_${SubEventDirection}`;

    return {
        status: tPages(`event.timeline.subevents.${statusKey}`),
        isInside: location === 'INSIDE'
    };
}

const ActivityItem: React.FC<ActivityItemProps> = ({
    rfidCode,
    eventTimestamp,
    deviceId,
    eventId,
    lastSubevent,
    location,
    locationSource,
    locationUpdatedAt,
    onShowRfidEvents,
    onHideRfidLastSeen,
    onSetRfidLastSeenLocation,
    isHidePending,
    isSetLocationPending
}) => {
    const { t: tPages } = usePageTranslation();
    const { t: tComponents } = useComponentTranslation();
    const profileQuery = useRfidProfileQuery(rfidCode, deviceId);
    const eventQuery = useDeviceEventQuery(deviceId, eventId);
    const avatarQuery = useRfidProfileAvatarQuery(
        profileQuery.data?.avatarUserId ?? undefined,
        rfidCode,
        profileQuery.data?.avatarUpdatedAt
    );

    const handleHideClick = () => {
        onHideRfidLastSeen?.({ deviceId, rfidCode, eventId });
    };

    const handleSetLocationClick = (location: RfidLastSeenLocation) => {
        onSetRfidLastSeenLocation?.({ deviceId, rfidCode, location });
    };

    if (profileQuery.isLoading || eventQuery.isLoading) {
        return (
            <div style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}>
                <IonSkeletonText animated style={{ width: '100%', height: '180px' }} />
            </div>
        );
    }

    const event = eventQuery.data;
    const { status, isInside } = getActivityStatus(location, locationSource, lastSubevent, tPages, tComponents);
    const outcomeDirection = locationSource === 'MANUAL' ? undefined : getOutcomeDirection(lastSubevent);

    const posterFrameSrc = event
        ? `${OCGatewayClient.apiURL}/events/${deviceId}/${eventId}/${event.posterFrameIndex || 0}${event.accessToken ? `?t=${event.accessToken}` : ''}`
        : undefined;

    return (
        <ActivityOverviewTile
            name={profileQuery.data?.label ?? undefined}
            status={status}
            timestamp={locationUpdatedAt ?? eventTimestamp}
            icon="/assets/activity/activity-icon.svg"
            posterFrameSrc={posterFrameSrc}
            avatarSrc={avatarQuery.data ?? undefined}
            isInside={isInside}
            outcomeDirection={outcomeDirection}
            location={location}
            rfidCode={rfidCode}
            deviceId={deviceId}
            eventId={eventId}
            showViewEventAction={locationSource !== 'MANUAL'}
            shortTimestamp
            onShowEventsClick={onShowRfidEvents}
            onSetLocationClick={handleSetLocationClick}
            onHideClick={handleHideClick}
            isSetLocationPending={isSetLocationPending}
            isHidePending={isHidePending}
        />
    );
};

const ActivityOverview: React.FC<ActivityOverviewProps> = ({ onShowRfidEvents }) => {
    const query = useRfidLastSeenQuery();
    const hideMutation = useHideRfidLastSeenMutation();
    const setLocationMutation = useSetRfidLastSeenLocationMutation();
    const [scrollContainerRef] = useAutoAnimate<HTMLDivElement>();
    const { presentResult } = useUX();
    const { t: tComponents } = useComponentTranslation();
    const { t: tCommon } = useCommonTranslation();
    const [presentAlert] = useIonAlert();

    const handleHideRfidLastSeen = (params: HideRfidLastSeenParams) => {
        presentAlert({
            header: tComponents('activity_overview_tile.hide_visitor_alert_header'),
            message: tComponents('activity_overview_tile.hide_visitor_alert_message'),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: tComponents('activity_overview_tile.hide_visitor'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            () => hideMutation.mutateAsync(params),
                            tComponents('activity_overview_tile.visitor_hidden')
                        );
                    }
                }
            ]
        });
    };

    const handleSetRfidLastSeenLocation = (params: SetRfidLastSeenLocationParams) => {
        presentResult(
            () => setLocationMutation.mutateAsync(params),
            tComponents('activity_overview_tile.location_updated')
        );
    };

    if (query.isError) {
        return null; // Don't show anything if there's an error
    }

    return (
        <div className={styles.activityOverview}>
            <div ref={query.isLoading ? undefined : scrollContainerRef} className={styles.scrollContainer}>
                {query.isLoading && (
                    // Show skeleton loaders while loading
                    Array.from({ length: 3 }).map((_, index) => (
                        <div key={`skeleton-${index}`} className={styles.activityItem} style={{ padding: '8px' }}>
                            <IonSkeletonText animated style={{ width: '100%', height: '180px' }} />
                        </div>
                    ))
                )}
                {query.data?.filter((item) => !isRfidLastSeenHidden(item)).map((item) => (
                    <div key={`${item.deviceId}:${item.rfidCode}`} className={styles.activityItem}>
                        <ActivityItem
                            {...item}
                            onShowRfidEvents={onShowRfidEvents}
                            onHideRfidLastSeen={handleHideRfidLastSeen}
                            onSetRfidLastSeenLocation={handleSetRfidLastSeenLocation}
                            isHidePending={hideMutation.isPending}
                            isSetLocationPending={setLocationMutation.isPending}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ActivityOverview;
